/**
 * Session-wide cache of the admin notification unread count.
 *
 * The header bell remounts on every route change (Page → Layout → Header), so
 * the count and the last-fetch time live here rather than in Header state:
 * a remounted Header renders the cached count immediately and only refetches
 * when the 60s poll interval has actually elapsed. The Notifications screen
 * decrements the count optimistically when a row is read so the badge drops
 * without waiting for the next poll.
 *
 * Plain module pub/sub (no React, no DataController import) so
 * `DataController.clearSession()` can reset it without a circular import.
 */
const listeners = new Set();

const EMPTY = Object.freeze({ count: 0, fetchedAt: null, loaded: false });
let snapshot = EMPTY;

function emit() {
    listeners.forEach((listener) => {
        try {
            listener(snapshot);
        } catch (error) {
            console.error('notifications.store listener failed', error);
        }
    });
}

/** @returns {{count: number, fetchedAt: number|null, loaded: boolean}} */
export function getUnreadSnapshot() {
    return snapshot;
}

/** Replace the count with a fresh server value. */
export function setUnreadCount(count) {
    const safe = Math.max(0, Number(count) || 0);
    snapshot = { count: safe, fetchedAt: Date.now(), loaded: true };
    emit();
}

/** Optimistic local decrement (row marked read). No-op before the first fetch. */
export function decrementUnread(n = 1) {
    if (!snapshot.loaded) return;
    snapshot = { ...snapshot, count: Math.max(0, snapshot.count - n) };
    emit();
}

/**
 * @param {(snapshot: {count: number, fetchedAt: number|null, loaded: boolean}) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeToUnread(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** Called from DataController.clearSession() on logout. */
export function resetUnread() {
    snapshot = EMPTY;
    emit();
}
